import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { olvidarObraActiva } from "@/lib/obra-activa";
import { purgeOfflineCache } from "@/lib/offline-cache";

/**
 * RolObra es el catálogo cerrado de roles que un usuario puede tener EN una obra
 * (people/rbac.go). El admin global viaja con rol "ADMIN" en cada obra (miembro
 * implícito de todas); el resto de miembros con uno de los cuatro roles por obra.
 */
export type RolObra = "ADMIN" | "ADMIN_OBRA" | "CUALIFICADO" | "AUTORIZADO" | "CONSULTA";

// Roles POR obra del dominio (people/rbac.go). El catálogo cerrado del backend es
// ADMIN_OBRA / CUALIFICADO / AUTORIZADO / CONSULTA; el admin global viaja con rol
// "ADMIN" en cada obra. Las constantes del front deben coincidir exactamente para
// que los gates por rol casen con la validación de servidor.
export const ROL_ADMIN_OBRA = "ADMIN_OBRA";
export const ROL_CUALIFICADO = "CUALIFICADO";
export const ROL_AUTORIZADO = "AUTORIZADO";
export const ROL_CONSULTA = "CONSULTA";

/**
 * ObraMembresia es una obra del usuario en /me: su identidad, el rol del usuario en
 * ella y si puede registrar LOTOs (regla puede_registrar calculada por obra en el
 * backend). Espejo de auth.MeObra (backend/internal/auth/dto.go).
 */
export type ObraMembresia = {
  id_obra: number;
  codigo_obra: string;
  nombre: string;
  rol: RolObra;
  puede_registrar: boolean;
  /**
   * documentacion_acreditativa_requerida (#435, R5) es cierto SOLO si al usuario le falta la
   * documentación acreditativa que su rol en esta obra (CUALIFICADO o AUTORIZADO) exige para
   * operar. Explica al trabajador por qué no ve los botones de operar.
   */
  documentacion_acreditativa_requerida: boolean;
};

/**
 * MeResponse es el estado de autenticación canónico (GET /api/v1/me). login,
 * cambiar-clave y nda/aceptar también lo devuelven, así que la caché de /me se
 * refresca desde cada una de esas respuestas.
 *
 * F7 · Multi-obra (#153/#156): el rol es POR obra. Los campos globales legados
 * (roles/autorizado_loto/vigente/puede_registrar) desaparecen; en su lugar viajan
 * `es_admin` (superadministrador global) y `obras[]` (obras del usuario con su rol y
 * si puede registrar en cada una).
 */
export type MeResponse = {
  id: number;
  nombre_usuario: string;
  nombre: string;
  apellidos: string;
  es_admin: boolean;
  obras: ObraMembresia[];
  debe_cambiar_password: boolean;
  nda_pendiente: boolean;
  nda_version_actual: string;
};

/** membresiaEnObra devuelve la membresía del usuario en una obra, o undefined si no lo es. */
export function membresiaEnObra(me: MeResponse, obraId: number): ObraMembresia | undefined {
  return me.obras.find((o) => o.id_obra === obraId);
}

/**
 * canManageObra es el gate de gestión de la obra: admin global o ADMIN_OBRA de esa obra.
 * Espejo de RequireObraAdmin del backend, que cubre las PERSONAS de la obra (miembros,
 * autorizaciones LOTO y avisos, #171/#255 D2), la importación masiva de maestros (D5) y
 * el borrado de fotos del LOTO (D4).
 *
 * Vive aquí, y no con los guards de ruta, porque es un predicado PURO sobre MeResponse:
 * no redirige ni navega (#383). Así el barril de UI puede consumirlo sin arrastrar el
 * router, y sigue habiendo una sola definición de la regla.
 */
export function canManageObra(me: MeResponse, obraId: number): boolean {
  return me.es_admin || membresiaEnObra(me, obraId)?.rol === ROL_ADMIN_OBRA;
}

/**
 * documentacionAcreditativaRequeridaEn lee el flag que /me calcula por obra (#435, R5): cierto
 * solo si al usuario le falta la documentación acreditativa que su rol en esa obra exige para
 * operar. Es UX (explica por qué no hay botones de operar); el gate real es el servidor.
 */
export function documentacionAcreditativaRequeridaEn(me: MeResponse, obraId: number): boolean {
  return membresiaEnObra(me, obraId)?.documentacion_acreditativa_requerida ?? false;
}

export type LoginInput = {
  nombre_usuario: string;
  password: string;
};

export type CambiarClaveInput = {
  password_actual: string;
  password_nueva: string;
};

// RecuperarInput / RestablecerInput son los payloads del flujo público de
// recuperación (POST /recuperar, POST /restablecer). El backend responde SIEMPRE de
// forma neutra a /recuperar (anti-enumeración), así que la mutación nunca distingue
// si el correo existe.
export type RecuperarInput = {
  email: string;
};

export type RestablecerInput = {
  token: string;
  password_nueva: string;
};

/**
 * InvitacionInfo describe la invitación válida que se canjea en `/invitacion?token=…`
 * (#239): a quién se invitó y con qué usuario entrará. El servidor solo la sirve ante un
 * token vivo de 256 bits, así que no permite enumerar cuentas. Espejo de
 * auth.InvitacionInfoResponse.
 */
export type InvitacionInfo = {
  nombre: string;
  apellidos: string;
  nombre_usuario: string;
  expira_en: string;
};

/** AceptarInvitacionInput es el payload de POST /invitacion/aceptar. */
export type AceptarInvitacionInput = {
  token: string;
  password: string;
};

export type NdaResponse = {
  version: string;
  texto: string;
  pendiente: boolean;
};

export const ME_QUERY_KEY = ["me"] as const;
export const NDA_QUERY_KEY = ["nda"] as const;

/**
 * meQueryOptions resuelve el estado de auth. Sin reintentos (un 401 es una
 * respuesta válida del flujo, no un fallo a reintentar) y sin caducidad por
 * tiempo: se refresca explícitamente tras cada mutación que devuelve MeResponse.
 */
export const meQueryOptions = queryOptions({
  queryKey: ME_QUERY_KEY,
  queryFn: () => api.get<MeResponse>("/me"),
  retry: false,
  staleTime: Number.POSITIVE_INFINITY,
});

export function useMe() {
  return useQuery(meQueryOptions);
}

/**
 * ndaQueryOptions lee el NDA vigente. No se cachea (gcTime 0): el documento es
 * sensible y puede cambiar de versión entre lecturas; se re-pide al entrar.
 */
export const ndaQueryOptions = queryOptions({
  queryKey: NDA_QUERY_KEY,
  queryFn: () => api.get<NdaResponse>("/nda"),
  retry: false,
  staleTime: 0,
  gcTime: 0,
});

export function useNda() {
  return useQuery(ndaQueryOptions);
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => api.post<MeResponse>("/login", input),
    onSuccess: (me) => {
      queryClient.setQueryData(ME_QUERY_KEY, me);
      // #252 (D9): quien entra no hereda la obra que dejó el anterior. En una tablet
      // compartida B puede iniciar sesión sin que A cerrara la suya, así que el olvido no
      // puede depender solo del logout.
      olvidarObraActiva();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ status: string }>("/logout"),
    onSuccess: () => {
      // Se vacía toda la caché en memoria y la caché de lecturas en disco (#51) para
      // que ningún dato operativo del usuario saliente sobreviva al siguiente en una
      // tablet compartida. clear() antes de purgar evita que el subscriptor de
      // persistencia vuelva a volcar queries operativas residuales tras la purga.
      queryClient.clear();
      void purgeOfflineCache();
      // #252: y la obra activa recordada, que es contexto de navegación del dispositivo:
      // en una tablet compartida el siguiente usuario no debe heredar la obra del anterior.
      olvidarObraActiva();
      // F4 (offline): la outbox de mutaciones pendientes NO se descarta aquí; vive en
      // su propia DB y se purga en su propio flujo namespaceado por usuario.
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CambiarClaveInput) => api.post<MeResponse>("/cambiar-clave", input),
    onSuccess: (me) => queryClient.setQueryData(ME_QUERY_KEY, me),
  });
}

// useRecuperar solicita el correo de recuperación. No toca la caché de /me (es un
// flujo anónimo, sin sesión) y su respuesta es siempre neutra.
export function useRecuperar() {
  return useMutation({
    mutationFn: (input: RecuperarInput) => api.post<{ status: string }>("/recuperar", input),
  });
}

// useRestablecer fija la nueva contraseña con el token del correo. Es anónima y
// cierra todas las sesiones del usuario en el backend; tras ella se inicia sesión.
export function useRestablecer() {
  return useMutation({
    mutationFn: (input: RestablecerInput) => api.post<{ status: string }>("/restablecer", input),
  });
}

// INVITACION_QUERY_KEY namespacea la validación del enlace por token: es una lectura
// anónima y efímera, fuera de la allowlist de persistencia offline (nunca se cachea en
// disco: es una credencial de un solo uso).
export const INVITACION_QUERY_KEY = ["invitacion"] as const;

/**
 * useValidarInvitacion comprueba el enlace al cargar `/invitacion` (#239, D13). El token
 * viaja en el CUERPO de un POST, nunca en la query del API, para no acabar en los logs de
 * acceso. Sin token no se consulta nada (la página muestra «enlace no válido»).
 */
export function useValidarInvitacion(token: string | undefined) {
  return useQuery({
    queryKey: [...INVITACION_QUERY_KEY, token],
    queryFn: () => api.post<InvitacionInfo>("/invitacion/validar", { token }),
    enabled: token !== undefined && token !== "",
    retry: false,
    // El enlace es una credencial de un solo uso: ni se cachea ni se reintenta sola.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: false,
  });
}

/**
 * useAceptarInvitacion canjea el enlace fijando la primera contraseña. Es anónima y NO
 * abre sesión: tras el éxito la página lleva al login.
 */
export function useAceptarInvitacion() {
  return useMutation({
    mutationFn: (input: AceptarInvitacionInput) =>
      api.post<{ status: string }>("/invitacion/aceptar", input),
  });
}

export function useAcceptNda() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (version: string) => api.post<MeResponse>("/nda/aceptar", { version }),
    onSuccess: (me) => queryClient.setQueryData(ME_QUERY_KEY, me),
  });
}
